import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';

export type UtilityVisibility = 'public' | 'private';
export type UtilityGistFormat = 'text' | 'markdown';

export interface UtilityObjectItem {
  id: string;
  visibility: UtilityVisibility;
  filename: string;
  contentType: string;
  sizeBytes: number;
  expiresAt: string | null;
  createdAt: string;
  publicUrl: string | null;
}

export interface UtilityGistListItem {
  id: string;
  slug: string;
  title: string;
  format: UtilityGistFormat;
  visibility: UtilityVisibility;
  expiresAt: string | null;
  createdAt: string;
}

export interface UtilityGistDetail extends UtilityGistListItem {
  content: string;
  viewUrl: string | null;
}

export interface PublicGist {
  id: string;
  title: string;
  content: string;
  format: UtilityGistFormat;
  createdAt: string;
}

const utilitiesKeys = {
  all: ['utilities'] as const,
  objects: () => [...utilitiesKeys.all, 'objects'] as const,
  gists: () => [...utilitiesKeys.all, 'gists'] as const,
  gist: (id: string) => [...utilitiesKeys.all, 'gist', id] as const,
  publicGist: (id: string) => [...utilitiesKeys.all, 'public-gist', id] as const,
};

async function getJson<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...opts });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const body = (await res.json()) as { ok: boolean; error?: string } & T;
  if (!body.ok) throw new Error(body.error ?? 'Request failed');
  return body;
}

// ── Objects ────────────────────────────────────────────────────────────────

export function useUtilityObjects(search?: string) {
  return useQuery({
    queryKey: [...utilitiesKeys.objects(), search ?? ''],
    queryFn: () =>
      getJson<{ data: UtilityObjectItem[] }>(
        `/org/utilities/objects${search ? `?search=${encodeURIComponent(search)}` : ''}`
      ),
    staleTime: 30_000,
  });
}

/** Fetch a fresh shareable/signed URL for an object. */
export async function getUtilityObjectUrl(id: string): Promise<string> {
  const body = await getJson<{ url: string }>(`/org/utilities/objects/${id}/url`);
  return body.url;
}

export function useDeleteUtilityObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      getJson(`/org/utilities/objects/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: utilitiesKeys.objects() }),
  });
}

// ── Gists ────────────────────────────────────────────────────────────────

export function useUtilityGists(search?: string) {
  return useQuery({
    queryKey: [...utilitiesKeys.gists(), search ?? ''],
    queryFn: () =>
      getJson<{ data: UtilityGistListItem[] }>(
        `/org/utilities/gists${search ? `?search=${encodeURIComponent(search)}` : ''}`
      ),
    staleTime: 30_000,
  });
}

export function useUtilityGist(id: string | null) {
  return useQuery({
    queryKey: utilitiesKeys.gist(id ?? ''),
    queryFn: () => getJson<{ data: UtilityGistDetail }>(`/org/utilities/gists/${id}`),
    enabled: !!id,
  });
}

export function useDeleteUtilityGist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getJson(`/org/utilities/gists/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: utilitiesKeys.gists() }),
  });
}

// ── Public (unauthenticated) gist viewer ─────────────────────────────────

export function usePublicGist(id: string) {
  return useQuery({
    queryKey: utilitiesKeys.publicGist(id),
    queryFn: () => getJson<{ data: PublicGist }>(`/public/utilities/gists/${id}`),
  });
}
